import { boundaryLoopOfFaceSet } from '../boundary.js';
import { edgeId, faceNormal, propagateExplicitEdgeIds } from '../edges.js';
import { coplanarRegion, inwardEdgeNormal } from '../regions.js';
import { cross3, dot3, normalize3, sub3 } from '../vector-math.js';

// Same tightness rationale as REGION_NORMAL_EPSILON: a shrunk face must come
// out genuinely flat and correctly oriented, not "close enough".
const INSET_NORMAL_EPSILON = 1e-6;

// A lower bound on `amount`, relative to the region's own LONGEST boundary
// edge (see `boundaryLoopEdgeLengthRange`) rather than an absolute length —
// so the same *proportional* inset is accepted or rejected the same way
// regardless of whether the model is authored at unit scale, millimeter
// scale, or kilometer scale. 1e-6 sits two orders of magnitude above
// float32's ~1.2e-7 relative precision, which is the specific failure this
// guards against: an amount small enough to build cleanly in this module's
// own float64 math, but small enough that `buildGeometryFromTopology`
// writing it into a `Float32Array` collapses the new geometry back onto the
// original boundary — zero-area rendered triangles, and (via
// `normalsShareASurface`'s zero-normal-matches-anything rule) a poisoned
// zero shading normal bleeding onto their non-degenerate neighbors. The
// longest edge is the right measure *here*: float32 precision loss is a
// function of a vertex's own coordinate magnitude, and the longest edge is
// the best cheap proxy this function has for "how large can a coordinate
// in this region plausibly be" — see `boundaryLoopEdgeLengthRange`.
const INSET_MIN_RELATIVE_AMOUNT = 1e-6;

// Replaces a plain absolute-zero check on a new triangle's cross-product
// length (this module's general `normalize3` returns null below an
// absolute 1e-9) for inset's own post-construction degeneracy test. A cross
// product's length has units of length^2 (~2x triangle area), so comparing
// it against an absolute constant makes "was this triangle collapsed by too
// large an amount" a function of the model's absolute scale rather than of
// the inset itself — a valid inset on a millimeter-scale mesh could be
// wrongly rejected as "too large". Scaling by a characteristic region
// length squared fixes that, but *which* length matters: this must be the
// region's SHORTEST boundary edge, not its longest. A wall triangle's own
// cross-product length is bounded by *its own* source edge, not by some
// unrelated edge elsewhere in the region — on an elongated region (e.g. a
// 10000x1 box face), the wall triangles built along the short (length-1)
// edges have cross-product lengths on the order of `1 * amount`, entirely
// independent of the long edge's length. Squaring the LONGEST edge here
// (an earlier version of this fix did exactly that) inflates the threshold
// far past what those short-edge walls can ever produce, wrongly rejecting
// every ordinary, correctly-oriented inset in a wide range as "too large" —
// confirmed on `addBox({size:[10000,1,1]})` inset by amounts as small as
// 0.01, against a true offset limit of 0.5. Squaring the shortest edge
// instead reproduces the original absolute tolerance at unit scale (where
// shortest and longest coincide, e.g. a square) and stays correct — and,
// critically, *local* — at any other scale or aspect ratio.
const INSET_AREA_EPSILON = 1e-9;

/**
 * The shortest and longest of the region's own boundary edges, computed in
 * one pass — the two characteristic lengths `INSET_MIN_RELATIVE_AMOUNT` and
 * `INSET_AREA_EPSILON` are each checked against (see their own comments for
 * why they deliberately use different ends of this range, not a single
 * shared scale). Every member face already passed `faceNormal`'s
 * nonzero-area check before this is called, and `boundaryLoopOfFaceSet`
 * already guarantees at least one boundary edge exists — and a face with
 * nonzero area cannot have a zero-length edge — so `min` is strictly
 * positive for any region reachable through `insetFace`/`insetRegion`.
 */
function boundaryLoopEdgeLengthRange(boundaryLoop, positionById) {
  let minEdgeLength = Infinity;
  let maxEdgeLength = 0;
  for (let i = 0; i < boundaryLoop.length; i++) {
    const a = positionById.get(boundaryLoop[i]);
    const b = positionById.get(boundaryLoop[(i + 1) % boundaryLoop.length]);
    const edgeLength = Math.hypot(...sub3(b, a));
    if (edgeLength < minEdgeLength) minEdgeLength = edgeLength;
    if (edgeLength > maxEdgeLength) maxEdgeLength = edgeLength;
  }
  return { minEdgeLength, maxEdgeLength };
}

/**
 * Miter-offset a single boundary vertex inward by `amount`: average the
 * inward normals of its two adjacent boundary edges, then scale that
 * averaged direction so the vertex's perpendicular distance from *both*
 * edges is exactly `amount` (the standard polygon-offset miter construction,
 * done directly in 3D since the whole boundary loop already lies in one
 * plane). `cos` below equals `|sin(interiorAngle / 2)|`, which is
 * well-conditioned everywhere except as the interior angle approaches 0°
 * (an outward needle-thin spike) or 360° (an inward hairline slit) — *not*
 * at a straight-through 180° vertex, where `cos` is at its maximum (1) and
 * the corner is in fact the best-conditioned case, reducing to a plain
 * perpendicular offset. Returns null only in that 0°/360° limit, where no
 * finite miter point exists — the caller turns that into a clear error
 * rather than silently producing a spike.
 */
function insetMiterPoint(referenceNormal, prevPos, pos, nextPos, amount) {
  const inPrev = inwardEdgeNormal(referenceNormal, prevPos, pos);
  const inNext = inwardEdgeNormal(referenceNormal, pos, nextPos);
  if (!inPrev || !inNext) return null;
  const offsetDir = normalize3([inPrev[0] + inNext[0], inPrev[1] + inNext[1], inPrev[2] + inNext[2]]);
  if (!offsetDir) return null;
  const cos = dot3(offsetDir, inPrev);
  if (cos < 1e-6) return null;
  const scale = amount / cos;
  return [pos[0] + offsetDir[0] * scale, pos[1] + offsetDir[1] * scale, pos[2] + offsetDir[2] * scale];
}

/**
 * The one inset algorithm — inset an explicit, caller-chosen *set* of
 * coplanar faces as a single region, in place. `insetFace` is the
 * single-face case; `insetRegion` discovers a connected coplanar region (via
 * `coplanarRegion`, the same discovery `extrudeRegion` uses) and passes its
 * face ids here.
 *
 * Unlike extrude, inset never leaves the region's plane: every boundary
 * vertex is duplicated once, miter-offset *inward* by `amount` within that
 * plane (see `insetMiterPoint`), and the source faces' own triangulation is
 * reused verbatim (same vertex order, no winding flip) to build a smaller
 * inner cap — so the cap is guaranteed to face the same way as the source.
 * A ring of wall faces is built along the outer boundary loop connecting
 * each original vertex to its inset duplicate, exactly the way
 * `extrudeFaceSet` builds its side walls, except both "top" and "bottom" of
 * each ring quad are coplanar with the source region — so the whole result
 * (ring + inner cap) is one continuously flat surface, just subdivided, with
 * no new hard edge (matching `buildGeometryFromTopology`'s flat-face
 * grouping, see `RENDER_NORMAL_EPSILON`).
 *
 * Only a region whose vertices are entirely on its own outer boundary is
 * supported — i.e. no interior (non-boundary) vertex — since there is no
 * defined inset offset for a vertex that doesn't sit on an edge of the
 * shrinking polygon. This covers an ordinary box quad and any region
 * `extrudeFaceSet` itself produces (its caps are always boundary-only
 * n-gons); a future operation that can leave an interior vertex (e.g. a
 * subdivide) would need its own handling here, not assumed to work for free.
 *
 * Every new face's normal is checked against the region's reference normal
 * after construction, before anything is written to `topology`: an `amount`
 * large enough to invert or fold the shrunk boundary (bigger than the
 * region's own geometry supports) flips at least one face's winding, and is
 * reported as a clear error instead of silently producing self-intersecting
 * geometry. That check is scale-relative (`INSET_AREA_EPSILON` against the
 * region's *shortest* boundary edge — see `boundaryLoopEdgeLengthRange` and
 * `INSET_AREA_EPSILON`'s own comment for why it deliberately isn't the
 * longest), not against an absolute constant, so it means the same thing at
 * any model scale, including an elongated one.
 *
 * `amount` also has a scale-relative *lower* bound (`INSET_MIN_RELATIVE_AMOUNT`,
 * checked against the region's *longest* boundary edge — again see its own
 * comment for why that end of the range is the right one here): one small
 * enough to build cleanly here but too small to survive
 * `buildGeometryFromTopology`'s float32 render buffer is rejected up front,
 * before any offset is computed, rather than silently shipping geometry
 * that collapses to zero area (and a poisoned zero shading normal on its
 * neighbors) the moment it's rendered.
 *
 * All-or-nothing, like `extrudeFaceSet`: every lookup, validation, and
 * offset computation happens before anything is written to `topology`.
 */
function insetFaceSet(topology, faceIds, amount, commandName = 'insetFaceSet') {
  if (!Array.isArray(faceIds) || faceIds.length === 0) {
    throw new Error(`${commandName} requires a non-empty array of face ids`);
  }
  const uniqueFaceIds = new Set(faceIds);
  if (uniqueFaceIds.size !== faceIds.length) {
    throw new Error(`${commandName} received duplicate face ids`);
  }
  for (const id of faceIds) {
    if (!topology.faces.some((f) => f.id === id)) throw new Error(`unknown face id '${id}'`);
  }

  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error(`${commandName} requires a positive finite inset amount`);
  }

  const faces = topology.faces.filter((f) => uniqueFaceIds.has(f.id));

  let referenceNormal;
  for (const face of faces) {
    const normal = faceNormal(topology, face.id);
    if (!referenceNormal) referenceNormal = normal;
  }

  const regionVertexOrder = [...new Set(faces.flatMap((face) => face.vertexIds))];
  const boundaryLoop = boundaryLoopOfFaceSet(faces, commandName);
  const boundarySet = new Set(boundaryLoop);
  if (regionVertexOrder.some((id) => !boundarySet.has(id))) {
    throw new Error(`${commandName} does not support a region with a vertex interior to its own boundary`);
  }
  if (boundaryLoop.length !== new Set(boundaryLoop).size) {
    throw new Error(`${commandName} region boundary revisits a vertex (not a simple polygon)`);
  }

  const positionById = new Map(topology.vertices.map((v) => [v.id, v.position]));
  const loopLength = boundaryLoop.length;

  // Scale-relative lower bound, checked before any offset is computed (see
  // INSET_MIN_RELATIVE_AMOUNT's own comment): an amount too small relative
  // to this region's own size would validate cleanly here but collapse once
  // rendered, so reject it now with an unambiguous "too small," rather than
  // letting it fall through to the post-construction check below (which can
  // only ever say "too large"). Deliberately checked against the LONGEST
  // boundary edge, not the shortest — see INSET_MIN_RELATIVE_AMOUNT's own
  // comment for why the two checks in this function use opposite ends of
  // `boundaryLoopEdgeLengthRange`.
  const { minEdgeLength, maxEdgeLength } = boundaryLoopEdgeLengthRange(boundaryLoop, positionById);
  if (amount < INSET_MIN_RELATIVE_AMOUNT * maxEdgeLength) {
    throw new Error(`${commandName} amount is too small for this region (the result would collapse to zero-area geometry at render precision)`);
  }

  const insetPositionOf = new Map();
  for (let i = 0; i < loopLength; i++) {
    const prevId = boundaryLoop[(i - 1 + loopLength) % loopLength];
    const id = boundaryLoop[i];
    const nextId = boundaryLoop[(i + 1) % loopLength];
    const point = insetMiterPoint(
      referenceNormal,
      positionById.get(prevId),
      positionById.get(id),
      positionById.get(nextId),
      amount,
    );
    if (!point) {
      throw new Error(`${commandName} could not compute an inset offset at vertex '${id}' (a spike or hairline-slit corner, near 0° or 360°)`);
    }
    insetPositionOf.set(id, point);
  }

  let nextVertexId = topology.nextVertexId;
  const duplicateOf = new Map();
  const newVertices = boundaryLoop.map((oldId) => {
    const newId = `v${nextVertexId++}`;
    duplicateOf.set(oldId, newId);
    return { id: newId, position: insetPositionOf.get(oldId) };
  });
  const newPositionById = new Map(newVertices.map((v) => [v.id, v.position]));

  let nextFaceId = topology.nextFaceId;
  const capFaces = faces.map((face) => ({
    id: `f${nextFaceId++}`,
    vertexIds: face.vertexIds.map((vertexId) => duplicateOf.get(vertexId)),
  }));

  const sourceEdges = new Map();
  for (const face of faces) {
    for (let i = 0; i < face.vertexIds.length; i++) {
      const a = face.vertexIds[i];
      const b = face.vertexIds[(i + 1) % face.vertexIds.length];
      const id = edgeId(a, b);
      const entry = sourceEdges.get(id);
      if (entry) entry.count++;
      else sourceEdges.set(id, { a, b, count: 1 });
    }
  }
  const explicitEdgeReplacements = new Map();
  for (const [id, { a, b, count }] of sourceEdges) {
    if (count > 1) {
      explicitEdgeReplacements.set(id, [edgeId(duplicateOf.get(a), duplicateOf.get(b))]);
    }
  }
  const explicitEdgeIds = propagateExplicitEdgeIds(topology.explicitEdgeIds, explicitEdgeReplacements);

  const wallFaces = [];
  for (let i = 0; i < boundaryLoop.length; i++) {
    const a = boundaryLoop[i];
    const b = boundaryLoop[(i + 1) % boundaryLoop.length];
    const aTop = duplicateOf.get(a);
    const bTop = duplicateOf.get(b);
    wallFaces.push({ id: `f${nextFaceId++}`, vertexIds: [a, b, bTop] });
    wallFaces.push({ id: `f${nextFaceId++}`, vertexIds: [a, bTop, aTop] });
  }

  // Guard against a self-intersecting/inverted result (amount too large for
  // this region) before writing anything: every new face must still face the
  // same way as the source region. Degeneracy is tested against
  // `minCrossLength` (scale-relative — see INSET_AREA_EPSILON), not
  // `normalize3`'s general absolute-1e-9 cutoff: the lower bound checked
  // above already rules out amount being the reason a triangle collapses
  // here, so by this point a collapsed or flipped triangle only means
  // amount was too large for this specific region's geometry — the
  // orientation check against `referenceNormal` is unchanged. Deliberately
  // the SHORTEST boundary edge, not the longest (contrast the lower-bound
  // check above): a wall triangle's cross-product length is bounded by its
  // own source edge, not by some unrelated edge elsewhere in the region, so
  // squaring the longest edge would inflate this threshold far past what a
  // short edge's own walls can ever produce — see INSET_AREA_EPSILON's own
  // comment for the confirmed false rejection this caused on an elongated
  // (e.g. 10000x1) region before this was corrected.
  const minCrossLength = INSET_AREA_EPSILON * minEdgeLength * minEdgeLength;
  const allPositionById = new Map([...positionById, ...newPositionById]);
  for (const face of [...capFaces, ...wallFaces]) {
    const [a, b, c] = face.vertexIds.map((id) => allPositionById.get(id));
    const cross = cross3(sub3(b, a), sub3(c, a));
    const crossLength = Math.hypot(...cross);
    const normal = crossLength < minCrossLength ? null : cross.map((n) => n / crossLength);
    if (!normal || dot3(normal, referenceNormal) < 1 - INSET_NORMAL_EPSILON) {
      throw new Error(`${commandName} amount is too large for this region (the result would be inverted or self-intersecting)`);
    }
  }

  topology.vertices.push(...newVertices);
  topology.faces = topology.faces.filter((f) => !uniqueFaceIds.has(f.id));
  topology.faces.push(...capFaces, ...wallFaces);
  topology.nextVertexId = nextVertexId;
  topology.nextFaceId = nextFaceId;
  if (explicitEdgeIds !== undefined) topology.explicitEdgeIds = explicitEdgeIds;

  return {
    removedFaceIds: faces.map((f) => f.id),
    capFaceIds: capFaces.map((f) => f.id),
    newVertexIds: newVertices.map((v) => v.id),
    newFaceIds: [...capFaces, ...wallFaces].map((f) => f.id),
  };
}

/**
 * Inset one triangular face by `amount`, in place. See `insetFaceSet` for
 * the actual algorithm — this is the single-face case, kept as its own
 * export for symmetry with `extrudeFace` and for callers (agent or UI) that
 * address one specific triangle and expect a single `capFaceId` back. For
 * "inset the visible face this triangle is part of," see `insetRegion`.
 */
export function insetFace(topology, faceId, amount) {
  const outcome = insetFaceSet(topology, [faceId], amount, 'insetFace');
  return {
    removedFaceId: outcome.removedFaceIds[0],
    capFaceId: outcome.capFaceIds[0],
    newVertexIds: outcome.newVertexIds,
    newFaceIds: outcome.newFaceIds,
  };
}

/**
 * Inset the connected coplanar region containing `faceId` — the visible face
 * a human clicked on, not just the one triangle the click happened to hit.
 * See `coplanarRegion` for discovery and `insetFaceSet` for the shared inset
 * algorithm; mirrors `extrudeRegion`'s composition of the two.
 */
export function insetRegion(topology, faceId, amount, options) {
  const regionFaceIds = coplanarRegion(topology, faceId, options);
  const outcome = insetFaceSet(topology, regionFaceIds, amount, 'insetRegion');
  return {
    removedFaceIds: outcome.removedFaceIds,
    capFaceIds: outcome.capFaceIds,
    // Same rationale as extrudeRegion's capFaceId: a deterministic single
    // representative for selection/chaining, since coplanarRegion rediscovers
    // the whole new inner region from any one face inside it.
    capFaceId: outcome.capFaceIds[0],
    newVertexIds: outcome.newVertexIds,
    newFaceIds: outcome.newFaceIds,
  };
}
